import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { fmtMoney, fmtDate, vehicleLabel } from "@/lib/format";
import { PAYMENT_METHODS } from "@/lib/constants";
import BookingFormDrawer from "@/components/BookingFormDrawer";
import RelatedVouchers from "@/components/RelatedVouchers";
import { printBookingReceipt } from "@/components/bookingReceipt";
import {
  Plus,
  CalendarCheck,
  XCircle,
  CheckCircle,
  CurrencyInr,
  Trash,
  Printer,
} from "@phosphor-icons/react";

const STATUS_BADGE = {
  active: { label: "ACTIVE", className: "bg-[#FFD700]/20 text-ink border-[#FFD700]" },
  delivered: { label: "DELIVERED", className: "bg-[#00B341]/15 text-[#00B341] border-[#00B341]" },
  cancelled: { label: "CANCELLED", className: "bg-canvas-panel text-ink-subtle border-canvas-line line-through" },
};

function StatusBadge({ status }) {
  const meta = STATUS_BADGE[status] || STATUS_BADGE.active;
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 border text-[10px] font-mono uppercase tracking-wider ${meta.className}`}
      data-testid={`booking-status-${status}`}
    >
      {meta.label}
    </span>
  );
}

function PaymentRow({ booking, payment, onDeleted }) {
  const handleDelete = async () => {
    if (!window.confirm(`Delete this ${fmtMoney(payment.amount)} payment?`)) return;
    try {
      await api.delete(`/bookings/${booking.id}/payments/${payment.id}`);
      toast.success("Payment removed");
      onDeleted();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  return (
    <li
      data-testid={`booking-payment-${payment.id}`}
      className="flex items-center justify-between gap-2 py-1.5 border-b border-canvas-line last:border-b-0 text-xs"
    >
      <div className="flex items-center gap-2 min-w-0">
        <CurrencyInr size={12} weight="bold" className="text-ink-muted shrink-0" />
        <span className="font-mono font-semibold text-ink">{fmtMoney(payment.amount)}</span>
        <span className="font-mono text-[10px] uppercase text-ink-muted">
          {payment.payment_method}
        </span>
        {payment.reference && (
          <span className="text-ink-subtle truncate font-mono">
            · ref {payment.reference}
          </span>
        )}
        {payment.note && (
          <span className="text-ink-subtle truncate">· {payment.note}</span>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className="font-mono text-[10px] text-ink-subtle">
          {fmtDate(payment.paid_on)}
        </span>
        {booking.status === "active" && (
          <button
            onClick={handleDelete}
            data-testid={`delete-payment-${payment.id}`}
            className="text-ink-subtle hover:text-destructive transition-colors"
            aria-label="Delete payment"
          >
            <Trash size={11} weight="bold" />
          </button>
        )}
      </div>
    </li>
  );
}

function AddPaymentInline({ bookingId, balance, onAdded }) {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("cash");
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const num = Number(amount);
    if (!num || num <= 0) {
      toast.error("Enter a positive amount.");
      return;
    }
    const ref = (reference || "").trim();
    if (method !== "cash" && !ref) {
      toast.error(`${method.toUpperCase()} payment needs a reference (UTR / txn id).`);
      return;
    }
    setSaving(true);
    try {
      await api.post(`/bookings/${bookingId}/payments`, {
        amount: num,
        payment_method: method,
        reference: ref,
        note,
      });
      toast.success("Payment recorded");
      setAmount("");
      setReference("");
      setNote("");
      onAdded();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const refRequired = method !== "cash";

  return (
    <form
      onSubmit={submit}
      data-testid={`add-payment-form-${bookingId}`}
      className="border border-dashed border-canvas-line p-3 grid grid-cols-12 gap-2 items-end mt-2"
    >
      <div className="col-span-3">
        <label className="block font-mono text-[9px] uppercase tracking-[0.2em] text-ink-muted mb-1">
          Amount (₹)
        </label>
        <input
          type="number"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder={balance > 0 ? `Up to ${fmtMoney(balance)}` : "Amount"}
          data-testid={`payment-amount-${bookingId}`}
          className="w-full px-2 py-1.5 bg-canvas border border-canvas-line text-xs focus:outline-none focus:border-ink"
        />
      </div>
      <div className="col-span-2">
        <label className="block font-mono text-[9px] uppercase tracking-[0.2em] text-ink-muted mb-1">
          Method
        </label>
        <select
          value={method}
          onChange={(e) => setMethod(e.target.value)}
          data-testid={`payment-method-${bookingId}`}
          className="w-full px-2 py-1.5 bg-canvas border border-canvas-line text-xs focus:outline-none focus:border-ink"
        >
          {PAYMENT_METHODS.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
      </div>
      <div className="col-span-3">
        <label className="block font-mono text-[9px] uppercase tracking-[0.2em] text-ink-muted mb-1">
          Reference {refRequired ? "(required)" : "(optional)"}
        </label>
        <input
          value={reference}
          onChange={(e) => setReference(e.target.value)}
          placeholder={refRequired ? "UTR / txn id / card slip" : "Slip / receipt no."}
          data-testid={`payment-reference-${bookingId}`}
          className="w-full px-2 py-1.5 bg-canvas border border-canvas-line text-xs focus:outline-none focus:border-ink"
        />
      </div>
      <div className="col-span-2">
        <label className="block font-mono text-[9px] uppercase tracking-[0.2em] text-ink-muted mb-1">
          Note
        </label>
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="optional"
          data-testid={`payment-note-${bookingId}`}
          className="w-full px-2 py-1.5 bg-canvas border border-canvas-line text-xs focus:outline-none focus:border-ink"
        />
      </div>
      <div className="col-span-2">
        <button
          type="submit"
          disabled={saving}
          data-testid={`add-payment-submit-${bookingId}`}
          className="w-full px-3 py-1.5 bg-ink text-white text-xs font-semibold hover:bg-brand transition-colors disabled:opacity-60"
        >
          {saving ? "…" : "Add payment"}
        </button>
      </div>
    </form>
  );
}

function BookingCard({ booking, customer, onChanged }) {
  const vehLabel = booking.package_name
    || vehicleLabel(booking.vehicle)
    || "—";
  const balance = Number(booking.balance_due || 0);
  const paid = Number(booking.total_paid || 0);

  const handleConvert = async () => {
    if (!window.confirm(`Convert this booking to a sale? The vehicle will be marked SOLD.`)) return;
    try {
      await api.post(`/bookings/${booking.id}/convert`);
      toast.success("Booking converted to sale");
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  const handleCancel = async () => {
    if (!window.confirm("Cancel this booking and release the vehicle?")) return;
    try {
      await api.post(`/bookings/${booking.id}/cancel`);
      toast.success("Booking cancelled");
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div
      data-testid={`booking-card-${booking.id}`}
      className="border border-canvas-line bg-canvas-card p-4"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-semibold text-ink">{vehLabel}</div>
            <StatusBadge status={booking.status} />
          </div>
          <div className="font-mono text-[11px] text-ink-muted mt-1">
            VIN {booking.vehicle?.vin || "—"} · Booked {fmtDate(booking.created_at)}
            {booking.expected_delivery_date && (
              <span className="ml-2">
                · <CalendarCheck size={10} weight="bold" className="inline" /> deliver{" "}
                {fmtDate(booking.expected_delivery_date)}
              </span>
            )}
          </div>
        </div>
        {booking.status === "active" && (
          <div className="flex items-center gap-2 shrink-0 flex-wrap">
            <button
              onClick={() => printBookingReceipt({ booking, customer })}
              data-testid={`print-booking-${booking.id}`}
              data-print-receipt="active"
              className="px-2.5 py-1.5 border border-canvas-line text-xs hover:border-ink flex items-center gap-1"
              title="Print receipt"
            >
              <Printer size={12} weight="bold" />
              Receipt
            </button>
            <button
              onClick={handleConvert}
              data-testid={`convert-booking-${booking.id}`}
              className="px-2.5 py-1.5 bg-ink text-white text-xs font-semibold hover:bg-brand transition-colors flex items-center gap-1"
            >
              <CheckCircle size={12} weight="bold" />
              Convert to sale
            </button>
            <button
              onClick={handleCancel}
              data-testid={`cancel-booking-${booking.id}`}
              className="px-2.5 py-1.5 border border-canvas-line text-xs hover:border-destructive hover:text-destructive flex items-center gap-1"
            >
              <XCircle size={12} weight="bold" />
              Cancel
            </button>
          </div>
        )}
        {booking.status !== "active" && (
          <button
            onClick={() => printBookingReceipt({ booking, customer })}
            data-testid={`print-booking-${booking.id}`}
            className="px-2.5 py-1.5 border border-canvas-line text-xs hover:border-ink flex items-center gap-1 shrink-0"
            title="Print receipt"
          >
            <Printer size={12} weight="bold" />
            Receipt
          </button>
        )}
      </div>

      <div className="grid grid-cols-3 gap-2 mb-3 font-mono text-[11px]">
        <div className="border border-canvas-line p-2">
          <div className="text-[9px] uppercase tracking-[0.2em] text-ink-muted">Total</div>
          <div className="font-semibold text-ink text-sm mt-0.5">
            {fmtMoney(booking.total_price)}
          </div>
        </div>
        <div className="border border-canvas-line p-2">
          <div className="text-[9px] uppercase tracking-[0.2em] text-ink-muted">Paid</div>
          <div className="font-semibold text-[#00B341] text-sm mt-0.5">{fmtMoney(paid)}</div>
        </div>
        <div className="border border-canvas-line p-2">
          <div className="text-[9px] uppercase tracking-[0.2em] text-ink-muted">Balance</div>
          <div
            className={`font-semibold text-sm mt-0.5 ${
              balance > 0 ? "text-ink" : "text-ink-subtle"
            }`}
          >
            {fmtMoney(balance)}
          </div>
        </div>
      </div>

      <div>
        <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-ink-muted mb-1">
          Payments ({booking.payments?.length || 0})
        </div>
        {(booking.payments || []).length === 0 ? (
          <div className="font-mono text-[11px] text-ink-subtle italic">
            No payments yet.
          </div>
        ) : (
          <ul className="border border-canvas-line px-3">
            {booking.payments.map((p) => (
              <PaymentRow
                key={p.id}
                booking={booking}
                payment={p}
                onDeleted={onChanged}
              />
            ))}
          </ul>
        )}
        {booking.status === "active" && (
          <AddPaymentInline
            bookingId={booking.id}
            balance={balance}
            onAdded={onChanged}
          />
        )}
      </div>

      {booking.notes && (
        <div className="mt-2 font-mono text-[11px] text-ink-muted">
          <span className="uppercase tracking-wider text-[9px]">Notes: </span>
          {booking.notes}
        </div>
      )}

      <div className="mt-3">
        <RelatedVouchers context="booking" record={booking} defaultCollapsed />
      </div>
    </div>
  );
}

export default function BookingsSection({ customerId, customer, customerStatus, bookings, onChanged }) {
  const [showForm, setShowForm] = useState(false);
  const canCreate = customerStatus !== "won";
  return (
    <div className="bg-canvas-card border border-canvas-line" data-testid="bookings-section">
      <div className="px-5 py-4 border-b border-canvas-line flex items-center justify-between">
        <div>
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-ink-muted mb-1">
            Bookings
          </div>
          <h3 className="font-display text-lg font-semibold text-ink">
            {bookings.length} booking{bookings.length === 1 ? "" : "s"}
          </h3>
        </div>
        {canCreate ? (
          <button
            onClick={() => setShowForm(true)}
            data-testid="new-booking-button"
            className="px-3 py-2 bg-ink text-white text-sm font-semibold hover:bg-brand transition-colors flex items-center gap-1.5"
          >
            <Plus size={12} weight="bold" />
            New booking
          </button>
        ) : (
          <span className="font-mono text-[10px] text-ink-subtle uppercase tracking-wider">
            Customer inactive
          </span>
        )}
      </div>
      <div className="p-4 space-y-3">
        {bookings.length === 0 ? (
          <div className="font-mono text-xs text-ink-subtle text-center py-6">
            No bookings yet. Reserve a vehicle to lock it for this customer.
          </div>
        ) : (
          bookings.map((b) => (
            <BookingCard key={b.id} booking={b} customer={customer} onChanged={onChanged} />
          ))
        )}
      </div>

      {showForm && (
        <BookingFormDrawer
          open
          customerId={customerId}
          onClose={() => setShowForm(false)}
          onSaved={() => {
            setShowForm(false);
            onChanged();
          }}
        />
      )}
    </div>
  );
}
